import React from 'react';
import { Star, AlertCircle, Loader } from 'lucide-react';
import { useSupabase } from '../../hooks/useSupabase';
import toast from 'react-hot-toast';
import type { Database } from '../../lib/supabase';

type Feedback = Database['public']['Tables']['feedback']['Row'];

interface FeedbackDrawerProps {
  interviewerId: number;
}

export default function FeedbackDrawer({ interviewerId }: FeedbackDrawerProps) {
  const [feedback, setFeedback] = React.useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { client } = useSupabase();

  React.useEffect(() => {
    loadFeedback();
  }, [interviewerId]);

  const loadFeedback = async () => {
    setError(null);
    try {
      const { data, error } = await client
        .from('feedback')
        .select('*')
        .eq('interviewer_id', interviewerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw new Error('Failed to load feedback');
      }

      setFeedback(data || []);
    } catch (error) {
      console.error('Error loading feedback:', error);
      setError(error instanceof Error ? error.message : 'Failed to load feedback');
      toast.error('Failed to load feedback');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="py-4 text-center text-sm text-gray-500">
        <Loader className="h-5 w-5 animate-spin mx-auto mb-2" />
        <p>Loading feedback...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <AlertCircle className="h-5 w-5 text-red-500 mx-auto mb-2" />
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (feedback.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-gray-500">
        No feedback received yet for this interviewer
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      {feedback.map((item) => (
        <div key={item.id} className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-4 w-4 ${
                    i < item.rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <div className="text-sm text-gray-500">
              {new Date(item.created_at).toLocaleDateString()}
            </div>
          </div>
          {item.comment && (
            <p className="mt-2 text-sm text-gray-700">{item.comment}</p>
          )}
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>{item.name || 'Anonymous'}</span>
            {item.user_email && <span>{item.user_email}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}